import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PRIORITIES } from "@/lib/taskConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const EVENT_TYPES = [
  { value: "meeting", label: "Reunión", icon: "👥" },
  { value: "appointment", label: "Cita", icon: "📌" },
  { value: "birthday", label: "Cumpleaños", icon: "🎂" },
  { value: "reminder", label: "Recordatorio", icon: "⏰" },
  { value: "other", label: "Otro", icon: "📅" },
];
export const EVENT_TYPE_MAP = Object.fromEntries(EVENT_TYPES.map((t) => [t.value, t]));

export const RECURRENCES = [
  { value: "none", label: "No se repite" },
  { value: "daily", label: "Diaria" },
  { value: "weekly", label: "Semanal" },
  { value: "biweekly", label: "Cada 2 semanas" },
  { value: "monthly", label: "Mensual" },
  { value: "yearly", label: "Anual" },
];
export const RECURRENCE_MAP = Object.fromEntries(RECURRENCES.map((r) => [r.value, r.label]));

const EventForm = ({ sources, defaultDate, defaultTime, event, onSaved, onClose }) => {
  const qc = useQueryClient();
  const [title, setTitle] = useState(event?.title || "");
  const [type, setType] = useState(event?.event_type || "meeting");
  const [calendar, setCalendar] = useState(event ? (event.workspace_id || "personal") : "personal");
  const [startDate, setStartDate] = useState(event?.start_date || defaultDate);
  const [endDate, setEndDate] = useState(event?.end_date || defaultDate);
  const [allDay, setAllDay] = useState(event ? event.all_day : !defaultTime);
  const [startTime, setStartTime] = useState(event?.start_time || defaultTime || "10:00");
  const [endTime, setEndTime] = useState(event?.end_time || (defaultTime ? `${String(Math.min(23, parseInt(defaultTime) + 1)).padStart(2, "0")}:00` : "11:00"));
  const [location, setLocation] = useState(event?.location || "");
  const [recurrence, setRecurrence] = useState(event?.recurrence || "none");
  const [reminder, setReminder] = useState(event?.reminder_day_before || false);
  const [description, setDescription] = useState(event?.description || "");
  const [attendees, setAttendees] = useState(event?.attendee_ids || []);
  const [saving, setSaving] = useState(false);

  const writableSources = sources.filter((s) => s.id === "personal" || !["guest"].includes(s.my_role));
  const isSpace = calendar !== "personal";

  const { data: members = [] } = useQuery({
    queryKey: ["members", calendar],
    queryFn: async () => (await api.get(`/workspaces/${calendar}/members`)).data,
    enabled: isSpace,
  });
  const activeMembers = useMemo(() => members.filter((m) => m.status === "active"), [members]);

  const toggleAttendee = (id) =>
    setAttendees((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const save = async () => {
    if (!title.trim()) return toast.error("El título es obligatorio");
    if (!startDate) return toast.error("Elige una fecha");
    setSaving(true);
    try {
      const payload = {
        title, description, event_type: type,
        start_date: startDate, end_date: endDate || startDate,
        all_day: allDay,
        start_time: allDay ? null : startTime, end_time: allDay ? null : endTime,
        location, attendee_ids: isSpace ? attendees : [],
        recurrence,
        reminder_day_before: reminder,
      };
      if (event) {
        await api.put(`/events/${event.id}`, payload);
        toast.success("Evento actualizado");
      } else {
        await api.post("/events", { ...payload, workspace_id: isSpace ? calendar : null });
        toast.success("Evento creado");
      }
      qc.invalidateQueries({ queryKey: ["calendar"] });
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Título</Label>
        <Input data-testid="event-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Reunión de vecinos" maxLength={200} autoFocus />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Tipo</Label>
          <Select value={type} onValueChange={(v) => {
            setType(v);
            if (v === "birthday" && !event && recurrence === "none") setRecurrence("yearly");
          }}>
            <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Calendario</Label>
          <Select value={calendar} onValueChange={(v) => { setCalendar(v); setAttendees([]); }} disabled={!!event}>
            <SelectTrigger data-testid="event-calendar-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {writableSources.map((s) => <SelectItem key={s.id} value={s.id}>{s.icon} {s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Inicio</Label>
          <Input data-testid="event-start-date" type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }} />
        </div>
        <div className="space-y-2">
          <Label>Fin</Label>
          <Input data-testid="event-end-date" type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={allDay} onCheckedChange={setAllDay} data-testid="event-allday-switch" />
        <Label className="cursor-pointer" onClick={() => setAllDay(!allDay)}>Todo el día</Label>
      </div>
      {!allDay && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Hora inicio</Label>
            <Input data-testid="event-start-time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Hora fin</Label>
            <Input data-testid="event-end-time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Ubicación (opcional)</Label>
          <Input data-testid="event-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ej: Casa de los abuelos" maxLength={200} />
        </div>
        <div className="space-y-2">
          <Label>Repetición</Label>
          <Select value={recurrence} onValueChange={setRecurrence}>
            <SelectTrigger data-testid="event-recurrence-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RECURRENCES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
        <Checkbox checked={reminder} onCheckedChange={(v) => setReminder(!!v)} data-testid="event-reminder-checkbox" />
        🔔 Avisarme el día antes
      </label>
      {isSpace && members.length > 0 && (
        <div className="space-y-2">
          <Label>Asistentes</Label>
          <div className="max-h-32 overflow-y-auto space-y-1.5 rounded-lg border p-2">
            {activeMembers.map((m) => (
              <label key={m.profile_id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={attendees.includes(m.profile_id)} onCheckedChange={() => toggleAttendee(m.profile_id)} data-testid={`event-attendee-${m.profile_id}`} />
                {m.name}
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-2">
        <Label>Descripción (opcional)</Label>
        <Textarea data-testid="event-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={1000} />
      </div>
      <Button className="w-full" onClick={save} disabled={saving} data-testid="event-save-btn">
        {saving ? "Guardando…" : event ? "Guardar cambios" : "Crear evento"}
      </Button>
    </div>
  );
};

const TaskForm = ({ sources, defaultDate, onClose }) => {
  const qc = useQueryClient();
  const spaces = sources.filter((s) => s.id !== "personal" && s.my_role !== "guest");
  const [wsId, setWsId] = useState(spaces[0]?.id || "");
  const [projectId, setProjectId] = useState("");
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState(defaultDate);
  const [priority, setPriority] = useState("medium");
  const [assignee, setAssignee] = useState("none");
  const [saving, setSaving] = useState(false);

  const { data: projects = [] } = useQuery({
    queryKey: ["projects", wsId],
    queryFn: async () => (await api.get(`/workspaces/${wsId}/projects`)).data,
    enabled: !!wsId,
  });
  const { data: members = [] } = useQuery({
    queryKey: ["members", wsId],
    queryFn: async () => (await api.get(`/workspaces/${wsId}/members`)).data,
    enabled: !!wsId,
  });
  const activeMembers = useMemo(() => members.filter((m) => m.status === "active"), [members]);

  const save = async () => {
    if (!title.trim()) return toast.error("El título es obligatorio");
    if (!projectId) return toast.error("Elige un proyecto");
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/tasks`, {
        title, due_date: dueDate || null, priority,
        assignee_id: assignee === "none" ? null : assignee,
      });
      toast.success("Tarea creada");
      qc.invalidateQueries({ queryKey: ["calendar"] });
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (spaces.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No tienes Espacios donde crear tareas.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Espacio</Label>
          <Select value={wsId} onValueChange={(v) => { setWsId(v); setProjectId(""); setAssignee("none"); }}>
            <SelectTrigger data-testid="caltask-space-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {spaces.map((s) => <SelectItem key={s.id} value={s.id}>{s.icon} {s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Proyecto</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger data-testid="caltask-project-select"><SelectValue placeholder="Elige proyecto" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.icon} {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Título</Label>
        <Input data-testid="caltask-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Comprar pintura" maxLength={300} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Fecha límite</Label>
          <Input data-testid="caltask-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Prioridad</Label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="caltask-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Asignar a</Label>
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger data-testid="caltask-assignee-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sin asignar</SelectItem>
            {activeMembers.map((m) => (
              <SelectItem key={m.profile_id} value={m.profile_id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button className="w-full" onClick={save} disabled={saving} data-testid="caltask-save-btn">
        {saving ? "Guardando…" : "Crear tarea"}
      </Button>
    </div>
  );
};

export const EventDialog = ({ open, onOpenChange, sources, defaultDate, defaultTime, event }) => {
  const [mode, setMode] = useState("event");
  useEffect(() => { if (open) setMode("event"); }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{event ? "Editar evento" : "Crear en el calendario"}</DialogTitle>
        </DialogHeader>
        {!event && (
          <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-secondary">
            {[["event", "📅 Evento"], ["task", "✅ Tarea"]].map(([v, l]) => (
              <button key={v} data-testid={`event-mode-${v}`} onClick={() => setMode(v)}
                className={`py-1.5 rounded-md text-sm font-medium transition-colors ${mode === v ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
                {l}
              </button>
            ))}
          </div>
        )}
        {mode === "event" || event ? (
          <EventForm sources={sources} defaultDate={defaultDate} defaultTime={defaultTime} event={event} onClose={() => onOpenChange(false)} />
        ) : (
          <TaskForm sources={sources} defaultDate={defaultDate} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
};
